import React, { Component } from 'react';
import { Row, Col, Alert, Modal } from 'reactstrap';
import { connect } from 'react-redux';
import classnames from 'classnames';

import CheckCircle from 'assets/images/check-circle-app-download-hint.svg?react';
import XIcon from 'assets/images/x_icon.svg?react';
import { SetIcon } from 'components/icons/SetIcon';
import { getImageCoordsForFocalPoint } from '../helpers/get-image-coords-for-focal-point';
import { getImageCoverSize } from '../helpers/get-image-cover-size';
import { FaArrowDown } from 'react-icons/fa';
import { FaAngleRight } from 'react-icons/fa6';

import androidHeaderImg from 'assets/images/app-installation/android_header.png';
import phoneIconImg from 'assets/images/app-installation/phone_icon.png';
import { getTranslationKey } from 'helpers/texting';
import { getCookie, setCookie } from 'helpers/cookie';
import { SelectionModalCard } from 'modules/ScrAppBookNew/components/SelectionModalCard';
import { Button } from 'modules/ScrAppBookNew/components/ui/Button';
import { uiSurface } from 'modules/ScrAppBookNew/components/ui/uiSurface';
import * as actions from '../old-store/actions';
import media from 'assets/css/media';
import styled, { css } from 'styled-components';
import { ICollection } from 'types/collection';
import { IImage } from 'old-store/types/gallery';

export const APP_INSTALLATION_PROMPT_DELAY_MS = 30_000;

const APP_INSTALLATION_PROMPT_SEEN_COOKIE_DAYS = 365;

export const getAppInstallationPromptSeenCookieName = (): string =>
  `appInstallationPromptSeen-${window.SITE_ID}`;

const getOpenChromeHref = (): string =>
  `intent://${window.location.href.replace(
    'https://',
    ''
  )}#Intent;scheme=https;package=com.android.chrome;end`;

const AppInstallationOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 999999;
  overflow-x: hidden;
  overflow-y: auto;
  background: #ffffff;
`;

// New design shows every page of the prompt as a pop-up instead of a white full-screen takeover.
// Values match the other new-design modals (SelectionModalCard / PhotographerModal). The
// .modal-content selector must outrank the templates' `.ScrAppBook.X .modal .modal-content
// { border-radius: 0 !important }` (0,4,0), so it is pinned to 0,5,0 below.
const AppInstallModal = styled(Modal)`
  && {
    width: auto;
    max-width: none;
    margin: 24px;
    /* .modal-dialog-centered derives its min-height from --bs-modal-margin (0.5rem), so
       overriding the margin without this leaves the box taller than the space it sits in and
       the card centres below the viewport's middle. */
    min-height: calc(100% - 48px);

    @media (min-width: ${media.desktop}) {
      width: 100%;
      max-width: 520px;
      min-height: calc(100% - 3.5rem);
      margin: 1.75rem auto;
    }
  }

  .modal && > .modal-content.ui-card-shell {
    max-height: calc(100vh - 48px);
    max-height: calc(100dvh - 48px);
    overflow: hidden !important;
    border: none !important;
    border-radius: 30px !important;
    background: ${uiSurface.background} !important;
    color: ${uiSurface.ink} !important;

    @media (min-width: ${media.desktop}) {
      max-height: calc(100vh - 3.5rem);
      max-height: calc(100dvh - 3.5rem);
    }
  }
`;

// The install manuals are long, so they scroll inside the card rather than growing it. min-height
// is what lets a flex child shrink below its content and actually scroll.
const AppInstallModalBody = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  padding: 30px 0;
  // The screens are sized to their content, so one unbreakable string — the raw gallery URL on
  // the "wrong browser" screen — would widen the card the same way the icon row did.
  overflow-wrap: anywhere;
  overflow-y: auto;
  overscroll-behavior-y: contain;
  -webkit-overflow-scrolling: touch;
`;

const AppInstallCloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 18px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border: none;
  background: none;
  color: ${uiSurface.ink};
  cursor: pointer;
`;

const AppInstallerHintContainer = styled.div`
  font-family: 'Noto Sans', sans-serif !important;
  font-weight: 400;
`;

const AppInstallerHintTitle = styled.p<{ $newDesign?: boolean }>`
  font-size: 28px;
  text-align: left;
  color: #1d1d1d !important;
  margin-bottom: 24px;
  padding-left: 16px;
  /* keeps the headline clear of the modal's ✕ */
  padding-right: ${({ $newDesign }) => ($newDesign ? '44px' : '16px')};
`;

const AppInstallerHintList = styled.ul`
  margin-bottom: 35px;
  padding-left: 16px;
  padding-right: 16px;
`;

const AppInstallerHintListItem = styled.li`
  list-style-type: none;
  font-size: 18px;
  text-align: left;
  color: #1d1d1d !important;
  font-weight: 300;
  word-spacing: 2px;
  display: flex;
  justify-content: start;
  align-items: start;
  gap: 9px;

  span {
    color: #1d1d1d !important;
  }
`;

const AppInstallerHintBtn = styled.button<{ $newDesign: boolean }>`
  text-align: center;
  padding: ${({ $newDesign }) => ($newDesign ? '6px 10px' : '10px 20px')};
  font-size: ${({ $newDesign }) => ($newDesign ? '16px' : '14px')};
  height: ${({ $newDesign }) => ($newDesign ? '47px' : 'auto')};
  background-color: #212020;
  color: white;
  border: ${({ $newDesign }) => ($newDesign ? '1px' : '4px')} solid #212020;
  border-radius: ${({ $newDesign }) => ($newDesign ? '24px' : '0')};
  width: 100%;
  letter-spacing: ${({ $newDesign }) => ($newDesign ? 'normal' : '1.26px')};
  font-family: ${({ $newDesign }) =>
    $newDesign
      ? "var(--font, 'Noto Sans Display', sans-serif)"
      : "'Noto Sans', sans-serif"} !important;
  transition: ${({ $newDesign }) =>
    $newDesign ? 'background-color 0.2s ease, color 0.2s ease' : 'none'};

  ${({ $newDesign }) =>
    $newDesign &&
    `
      &:hover,
      &:focus {
        background-color: #ffffff;
        color: #000000;
      }
    `}
`;

const WrongBrowserContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 25px 30px 30px;
  text-align: left;
  overflow-wrap: anywhere;

  p {
    margin: 0;
    font-size: 16px;
    font-weight: 400;
    line-height: 1.5;
    color: ${uiSurface.ink} !important;
  }
`;

const WrongBrowserUrl = styled.p`
  padding: 12px 16px;
  border-radius: 15px;
  background: ${uiSurface.raised};
`;

const WrongBrowserButton = styled(Button)`
  margin-top: 8px;
  text-decoration: none;
`;

const AppInstallerHintLink = styled.p`
  text-align: center;
  color: #9b9a9a !important;
  cursor: pointer;
  text-decoration: underline;
  margin-top: 21px;
  text-underline-offset: 6px;
  opacity: 0.75;
  font-family: 'Noto Sans', sans-serif !important;
`;

const AppInstallerListIconContainer = styled.span`
  display: flex;
  justify-content: start;
  align-items: center;
  height: 27px;
  font-family: 'Noto Sans', sans-serif !important;
`;

const AppInstallerListIcon = styled(CheckCircle)`
  text-align: center;
  vertical-align: middle;
  font-size: 20px;
  color: #212020;
`;

const AppInstallerGalleryIconContainer = styled.div`
  background-color: #f9f9f9;
  border-radius: 22px;
  padding: 36px 20px 24px;
  display: flex;
  justify-content: start;
  align-items: center;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  box-sizing: border-box;
`;

const AppInstallerGalleryIconText = styled.p`
  color: #1d1d1d;
  font-size: 14px;
  font-family: 'Noto Sans', sans-serif !important;
`;

const AppInstallerGalleryIconsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: start;
  gap: 17px;
  width: 100%;
`;

const galleryIconBox = css`
  flex: 0 1 5em;
  min-width: 0;
  aspect-ratio: 1;
  border-radius: 1em;
  padding: 0;
  border: 0;
`;

const AppInstallerGalleryIcon = styled.div`
  ${galleryIconBox};
  position: relative;
  box-shadow: 0 0.2em 0.4em #0000003e, inset 0 0.07em 0 #0000003e;
`;

// Pinned to the edges, not height: 100% — WebKit doesn't resolve a percentage height against a
// parent sized only by aspect-ratio, so on iPhones the icon collapsed to 0px.
const AppInstallerGalleryMainIconContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-repeat: no-repeat;
  border-radius: 1em;
`;

const AppInstallerGalleryIconSkeleton = styled.div`
  ${galleryIconBox};
  background-color: #ededed;
`;

const IOSContainerSecondScreen = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: start;
  font-family: 'Noto Sans', sans-serif !important;
`;

const SecondScreenTitle = styled.h2`
  color: #1d1d1d !important;
  font-size: 28px !important;
  font-family: 'Noto Sans', sans-serif !important;
  margin-bottom: 20px;
  padding-left: 16px;
  padding-right: 16px;
`;

const SecondScreenList = styled.ol`
  font-family: 'Noto Sans', sans-serif !important;
  font-weight: 300;
  padding-left: 16px;
  padding-right: 16px;

  li {
    display: flex;
    justify-content: start;
    align-items: start;
    gap: 16px;
    flex-direction: column;
    color: #1d1d1d !important;
    font-size: 15px !important;
    text-align: left;
    padding-bottom: 24px;
    font-weight: 300;

    &:last-child {
      padding-bottom: 0;
    }

    img {
      width: 100%;
      border-radius: 25px;
    }
  }
`;

const StepWrap = styled.div`
  display: flex;
`;

const ActionBox = styled.div`
  background: #f9f9f9;
  padding: 15px 10px;
  width: 100%;
  border-radius: 15px;
  display: flex;
  align-items: center;
`;

const PhoneIconWrap = styled.div`
  width: 25px;
  height: 25px;
  margin-right: 10px;

  img {
    width: 100%;
    height: 100%;
  }
`;

const ImageIconWrap = styled(PhoneIconWrap)`
  background-repeat: no-repeat;
  border-radius: 10px;
  width: 38px;
  height: 38px;
  position: relative;
  box-shadow: 0 0 11px rgba(0, 0, 0, 0.4);
`;

const DownloadBadge = styled.div`
  background: #fff;
  border-radius: 50%;
  width: 14px;
  height: 14px;
  bottom: 2px;
  right: 2px;
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 9px;
    height: 9px;
  }
`;

const InstallAction = styled.span`
  position: relative;

  &:after {
    content: '';
    position: absolute;
    width: 70%;
    height: calc(100% + 40px);
    border: 1px solid #000;
    border-radius: 50%;
    left: 15%;
    top: -20px;
  }
`;

interface IEnvironment {
  isIDevice: boolean;
  isAndroidDevice: boolean;
  displayWebAppInstallation: boolean;
  isNotChromeOnAndroid: boolean;
  isNotSafariOnIos: boolean;
}

interface IImagesState {
  images: IImage[];
}

interface IUserState {
  appInstallation: string;
  appInstallationAlertHint?: boolean;
}

interface IAppInstallationScrAppBookPageProps {
  collection: ICollection;
  images: IImagesState;
  environment: IEnvironment;
  user: IUserState;
  setAppInstallationState: (state: string) => void;
  showAppInstallationAlertHint: () => void;
}

interface IAppInstallationScrAppBookPageState {
  selectedDevice: 'ios' | 'android' | null;
  isPromptVisible: boolean;
}

interface IAppIconStyles {
  backgroundImage: string;
  backgroundPosition: string;
  backgroundSize: string;
}

export class AppInstallationScrAppBookPage extends Component<
  IAppInstallationScrAppBookPageProps,
  IAppInstallationScrAppBookPageState
> {
  private icon: IImage | null = null;

  private promptTimer: number | null = null;

  private addedOverlayBodyClass = false;

  constructor(props: IAppInstallationScrAppBookPageProps) {
    super(props);

    this.state = {
      selectedDevice: null,
      isPromptVisible: props.user.appInstallation !== 'unset'
    };

    this.declineAppInstallation = this.declineAppInstallation.bind(this);
    this.acceptAppInstallation = this.acceptAppInstallation.bind(this);
    this.selectDevice = this.selectDevice.bind(this);
    this.showDelayedPrompt = this.showDelayedPrompt.bind(this);
  }

  componentDidMount(): void {
    const {
      user: { appInstallation }
    } = this.props;

    if (appInstallation !== 'unset') {
      this.addOverlayBodyClass();

      return;
    }

    if (getCookie(getAppInstallationPromptSeenCookieName())) {
      return;
    }

    this.promptTimer = window.setTimeout(this.showDelayedPrompt, APP_INSTALLATION_PROMPT_DELAY_MS);
  }

  componentDidUpdate(previousProps: IAppInstallationScrAppBookPageProps): void {
    const previousState = previousProps.user.appInstallation;
    const currentState = this.props.user.appInstallation;
    const hasInstallationStateChanged = previousState !== currentState;
    const isInstallationRequested = currentState !== 'unset';
    const shouldShowPrompt =
      hasInstallationStateChanged && isInstallationRequested && !this.state.isPromptVisible;

    if (shouldShowPrompt) {
      if (this.promptTimer !== null) {
        window.clearTimeout(this.promptTimer);
        this.promptTimer = null;
      }

      this.addOverlayBodyClass();
      this.setState({ isPromptVisible: true });
    }
  }

  componentWillUnmount(): void {
    if (this.promptTimer !== null) {
      window.clearTimeout(this.promptTimer);
    }

    if (this.addedOverlayBodyClass) {
      document.body.classList.remove('white-background');
    }
  }

  // Class component, so the flag is read off the collection rather than through useIsNewDesign().
  private get isNewDesign(): boolean {
    return (this.props.collection as any).collectionCreator?.featureFlags?.newDesign === true;
  }

  getAppIcon(): IImage | undefined {
    const {
      images: { images = [] }
    } = this.props;

    const appIcon = images.find((image) => image.isIcon) || images[0];

    return appIcon;
  }

  getAppIconStyles({
    widthParam = 80,
    heightParam = 80
  }: {
    widthParam?: number;
    heightParam?: number;
  }): IAppIconStyles {
    if (!this.icon) {
      this.icon = this.getAppIcon() || null;
    }

    if (!this.icon) {
      return {
        backgroundImage: '',
        backgroundPosition: '50% 50%',
        backgroundSize: `${widthParam}px ${heightParam}px`
      };
    }

    const { width, height } = getImageCoverSize({
      image: {
        width: this.icon?.width || widthParam,
        height: this.icon?.height || heightParam,
        focalPoint: { xPercent: 50, yPercent: 50 }
      },
      view: { width: widthParam, height: heightParam },
      viewForPositionCalculation: { width: widthParam, height: heightParam },
      zoomToFit: false
    });

    const { x, y } = getImageCoordsForFocalPoint({
      image: { width, height, focalPoint: { xPercent: 50, yPercent: 50 } },
      view: { width: widthParam, height: heightParam }
    });

    const styles: IAppIconStyles = {
      backgroundImage: `url(${this.icon.url})`,
      backgroundPosition: `${x}px ${y}px`,
      backgroundSize: `${width}px ${height}px`
    };

    return styles;
  }

  // Same device priority as showAppInstallationManual: iOS first, then Android.
  getNewWrongBrowser(): {
    hintKey: string;
    buttonKey: string;
    linkProps: { href: string; target?: string; rel?: string };
  } | null {
    const { environment } = this.props;
    const { selectedDevice } = this.state;

    if (selectedDevice === 'ios' || environment.isIDevice) {
      return environment.isNotSafariOnIos
        ? {
            hintKey: 'appinstallation.ios.openInSafari',
            buttonKey: 'appinstallation.ios.openSafari',
            linkProps: { href: 'x-web-search://', target: '_blank', rel: 'noopener noreferrer' }
          }
        : null;
    }

    if (selectedDevice === 'android' || environment.isAndroidDevice) {
      return environment.isNotChromeOnAndroid
        ? {
            hintKey: 'appinstallation.android.openInChrome',
            buttonKey: 'appinstallation.android.openChrome',
            linkProps: { href: getOpenChromeHref() }
          }
        : null;
    }

    return null;
  }

  private addOverlayBodyClass(): void {
    // The new design renders a modal over the gallery, so the page behind must not go white.
    if (this.isNewDesign) return;

    if (!document.body.classList.contains('white-background')) {
      document.body.classList.add('white-background');
      this.addedOverlayBodyClass = true;
    }
  }

  private showDelayedPrompt(): void {
    this.promptTimer = null;
    setCookie(
      getAppInstallationPromptSeenCookieName(),
      'true',
      APP_INSTALLATION_PROMPT_SEEN_COOKIE_DAYS
    );
    this.addOverlayBodyClass();
    this.setState({ isPromptVisible: true });
  }

  selectDevice(selectedDevice: 'ios' | 'android'): void {
    this.setState({ selectedDevice });
  }

  declineAppInstallation(): void {
    // The new design closes with the ✕ and drops the yellow follow-up notification.
    if (!this.isNewDesign) this.props.showAppInstallationAlertHint();

    this.props.setAppInstallationState('declined');
  }

  acceptAppInstallation(): void {
    if (window.deferredPrompt) {
      window.deferredPrompt.prompt();
      // Wait for the user to respond to the prompt
      window.deferredPrompt.userChoice.then((choiceResult: { outcome: string }) => {
        if (choiceResult.outcome === 'accepted') {
          window.logToServer('User accepted the A2HS prompt');
        } else {
          window.logToServer('User dismissed the A2HS prompt');
          this.declineAppInstallation();
        }
        window.deferredPrompt.deferredPrompt = null;
      });
    } else {
      this.props.setAppInstallationState('accepted');
    }
  }

  showAppInstallationManual(): React.ReactNode {
    const { environment } = this.props;
    const { selectedDevice } = this.state;

    if (selectedDevice === 'ios' || environment.isIDevice) {
      return this.renderIosAppInstallationManual(environment);
    }
    if (selectedDevice === 'android' || environment.isAndroidDevice) {
      return this.renderAndroidAppInstallationManual(environment);
    }

    return this.renderNotSupportedDevice();
  }

  renderNotSupportedDevice(): React.ReactNode {
    return (
      <div>
        <Alert color="danger">{getTranslationKey('appinstallation.notSupportedDevice')}</Alert>
        <p className="m-t-10">
          {getTranslationKey('appinstallation.notSupportedDeviceDescription1')}
          <br />
          <br />
          {getTranslationKey('appinstallation.notSupportedDeviceDescription2')}
        </p>
        <button onClick={() => this.selectDevice('ios')} className="site-button w-100-p">
          {getTranslationKey('appinstallation.ios.device')}
        </button>
        <button onClick={() => this.selectDevice('android')} className="site-button w-100-p m-t-10">
          {getTranslationKey('appinstallation.android.device')}
        </button>
      </div>
    );
  }

  renderAndroidAppInstallationManual(environment: IEnvironment): React.ReactNode {
    if (environment.isNotChromeOnAndroid) {
      return (
        <div>
          <Alert color="danger">{getTranslationKey('appinstallation.wrongBrowser')}</Alert>
          <p className="m-t-10">{getTranslationKey('appinstallation.android.openInChrome')}</p>
          <p className="m-t-10">{window.location.href.replace(window.location.search, '')}</p>
          <a className="site-button" href={getOpenChromeHref()}>
            {getTranslationKey('appinstallation.android.openChrome')}
          </a>
        </div>
      );
    }

    const styles = this.getAppIconStyles({ widthParam: 38, heightParam: 38 });

    return (
      <div>
        <SecondScreenTitle>
          {getTranslationKey('appinstallation.android.newTitle')}
        </SecondScreenTitle>
        <SecondScreenList>
          <li>
            <StepWrap>
              <span className="me-2">1.</span>
              <span>{getTranslationKey('appinstallation.android.manualStep1')}</span>
            </StepWrap>
            <img src={androidHeaderImg} alt="" />
          </li>
          <li>
            <StepWrap>
              <span className="me-2">2.</span>
              <span>{getTranslationKey('appinstallation.android.manualStep2')}</span>
            </StepWrap>
            <ActionBox>
              <PhoneIconWrap>
                <img src={phoneIconImg} alt="" />
              </PhoneIconWrap>
              <span>{getTranslationKey('appinstallation.android.goHomeText')}</span>
            </ActionBox>
          </li>
          <li>
            <StepWrap>
              <span className="me-2">3.</span>
              <span>{getTranslationKey('appinstallation.android.manualStep3')}</span>
            </StepWrap>
            <ActionBox>
              <ImageIconWrap style={styles}>
                <DownloadBadge>
                  <FaArrowDown />
                </DownloadBadge>
              </ImageIconWrap>
              <span>{getTranslationKey('appinstallation.android.install')}</span>
              <span className="ms-auto">
                <FaAngleRight />
              </span>
            </ActionBox>
          </li>
          <li>
            <StepWrap>
              <span className="me-2">4.</span>
              <span>{getTranslationKey('appinstallation.android.manualStep4')}</span>
            </StepWrap>
            <ActionBox>
              <span className="w-50-p text-center">
                {getTranslationKey('appinstallation.android.cancel')}
              </span>
              <InstallAction className="w-50-p text-center">
                {getTranslationKey('appinstallation.android.install')}
              </InstallAction>
            </ActionBox>
          </li>
          <li>
            <StepWrap>
              <span className="me-2">5.</span>
              <span>{getTranslationKey('appinstallation.android.manualStep5')}</span>
            </StepWrap>
          </li>
          {this.renderIcon()}
        </SecondScreenList>
      </div>
    );
  }

  renderIosAppInstallationManual(environment: IEnvironment): React.ReactNode {
    if (environment.isNotSafariOnIos) {
      return (
        <div>
          <Alert color="danger">{getTranslationKey('appinstallation.wrongBrowser')}</Alert>
          <p className="m-t-10">{getTranslationKey('appinstallation.ios.openInSafari')}</p>
          <p className="m-t-10">{window.location.href.replace(window.location.search, '')}</p>
          <a
            className="site-button"
            href="x-web-search://"
            target="_blank"
            rel="noopener noreferrer"
          >
            {getTranslationKey('appinstallation.ios.openSafari')}
          </a>
        </div>
      );
    }

    const isGerman = window.LANGUAGE === 0;
    const screenSuffix = isGerman ? '_de' : '';

    return (
      <IOSContainerSecondScreen>
        <SecondScreenTitle>{getTranslationKey('appinstallation.headlineManual')}</SecondScreenTitle>

        <SecondScreenList>
          <li>
            1. {getTranslationKey('appinstallation.ios.manual1')}
            <img src="/assets/images/app-installation/ios1screen.png" alt="" />
          </li>
          <li>
            2. {getTranslationKey('appinstallation.ios.manual2')}
            <img src={`/assets/images/app-installation/ios2screen${screenSuffix}.png`} alt="" />
          </li>
          <li>
            3. {getTranslationKey('appinstallation.ios.manual3')}
            <img src={`/assets/images/app-installation/ios3screen${screenSuffix}.png`} alt="" />
          </li>
          <li>
            4. {getTranslationKey('appinstallation.ios.manual4')}
            <img src={`/assets/images/app-installation/ios4screen${screenSuffix}.png`} alt="" />
          </li>
          <li>5. {getTranslationKey('appinstallation.ios.manual5')}</li>
          {this.renderIcon()}
        </SecondScreenList>
        <hr />
      </IOSContainerSecondScreen>
    );
  }

  renderIcon(): React.ReactNode {
    const { collection, images } = this.props;

    if (images && images.images && images.images.length) {
      const appIconStyles = this.getAppIconStyles({});

      return (
        <AppInstallerGalleryIconContainer>
          <AppInstallerGalleryIconsContainer>
            <AppInstallerGalleryIconSkeleton />
            <AppInstallerGalleryIcon>
              <AppInstallerGalleryMainIconContainer style={appIconStyles} />
            </AppInstallerGalleryIcon>
            <AppInstallerGalleryIconSkeleton />
          </AppInstallerGalleryIconsContainer>
          <AppInstallerGalleryIconText>
            {getTranslationKey('appinstallation.galleryTitle', { galleryName: collection.title })}
          </AppInstallerGalleryIconText>
        </AppInstallerGalleryIconContainer>
      );
    }

    return null;
  }

  renderAppInstallationHint(): React.ReactNode {
    const { isNewDesign } = this;
    const titleKey = isNewDesign
      ? 'appinstallation.newDesign.title'
      : 'appinstallation.descriptionScrAppBook';
    const benefitsKey = isNewDesign
      ? 'appinstallation.newDesign.benefits'
      : 'appinstallation.benefits';
    const benefits = getTranslationKey(benefitsKey);

    return (
      <AppInstallerHintContainer>
        <AppInstallerHintTitle $newDesign={isNewDesign}>
          {getTranslationKey(titleKey)}
        </AppInstallerHintTitle>

        <AppInstallerHintList>
          {Object.keys(benefits).map((benefit) => (
            <AppInstallerHintListItem key={benefit}>
              <AppInstallerListIconContainer>
                <AppInstallerListIcon />
              </AppInstallerListIconContainer>
              <span>{benefits[benefit]}</span>
            </AppInstallerHintListItem>
          ))}
        </AppInstallerHintList>

        {this.renderIcon()}
        <div className="mt-4">
          <AppInstallerHintBtn
            type="button"
            $newDesign={isNewDesign}
            onClick={this.acceptAppInstallation}
          >
            {getTranslationKey('appinstallation.showManual').toUpperCase()}
          </AppInstallerHintBtn>
          {/* The new design dismisses with the ✕ instead. */}
          {!isNewDesign && (
            <AppInstallerHintLink
              onClick={this.declineAppInstallation}
              onKeyDown={this.declineAppInstallation}
            >
              {getTranslationKey('appinstallation.continueInBrowser')}
            </AppInstallerHintLink>
          )}
        </div>
      </AppInstallerHintContainer>
    );
  }

  render(): React.ReactNode {
    const {
      user: { appInstallation },
      collection: { templateName }
    } = this.props;
    const { isPromptVisible } = this.state;

    if (!isPromptVisible || window.location.pathname.includes('/photobook')) return null;

    const page =
      appInstallation === 'unset'
        ? this.renderAppInstallationHint()
        : this.showAppInstallationManual();

    const newWrongBrowser =
      this.isNewDesign && appInstallation !== 'unset' ? this.getNewWrongBrowser() : null;

    if (newWrongBrowser) {
      return (
        <SelectionModalCard
          narrow
          isOpen
          close={this.declineAppInstallation}
          dataCy="app-installation-wrong-browser"
          icon={null}
          title={getTranslationKey('appinstallation.wrongBrowser')}
        >
          <WrongBrowserContent>
            <p>{getTranslationKey(newWrongBrowser.hintKey)}</p>
            <WrongBrowserUrl>
              {window.location.href.replace(window.location.search, '')}
            </WrongBrowserUrl>
            <WrongBrowserButton
              variant="primary"
              fullWidth
              data-cy="app-installation-wrong-browser-open"
              {...newWrongBrowser.linkProps}
            >
              {getTranslationKey(newWrongBrowser.buttonKey)}
            </WrongBrowserButton>
          </WrongBrowserContent>
        </SelectionModalCard>
      );
    }

    if (this.isNewDesign) {
      return (
        <AppInstallModal
          centered
          isOpen
          zIndex={999999}
          toggle={this.declineAppInstallation}
          // opts out of the global .modal-content fill (styles/index.scss); colours set above
          contentClassName="ui-card-shell"
        >
          <AppInstallModalBody
            className="app-installation-container"
            data-cy="app-installation-prompt"
          >
            <AppInstallCloseButton
              type="button"
              onClick={this.declineAppInstallation}
              aria-label={getTranslationKey('closeWindow')}
              data-cy="app-installation-close"
            >
              <SetIcon slug="close-x" fallback={XIcon} width={22} height={22} />
            </AppInstallCloseButton>
            {page}
          </AppInstallModalBody>
        </AppInstallModal>
      );
    }

    return (
      <AppInstallationOverlay
        className="app-installation-container white-background h-100 w-100"
        data-cy="app-installation-prompt"
      >
        <Row className="justify-content-center text-center">
          <Col className="m-t-20" xl="4" lg="6" md="6" sm="12" xs="12">
            {page}

            {appInstallation !== 'unset' && (
              <p
                onClick={this.declineAppInstallation}
                onKeyDown={this.declineAppInstallation}
                className={classnames('m-t-15 cursor-pointer sticky-bottom-center p-b-20', {
                  'white-background': templateName && !templateName.includes('SpringVibes')
                })}
              >
                <AppInstallerHintLink>
                  {getTranslationKey('appinstallation.abortInstallation')}
                </AppInstallerHintLink>
              </p>
            )}
          </Col>
        </Row>
      </AppInstallationOverlay>
    );
  }
}

interface IRootState {
  collection: ICollection;
  images: IImagesState;
  environment: IEnvironment;
  user: IUserState;
}

function mapStateToProps({ collection, images, environment, user }: IRootState) {
  return { collection, images, user, environment };
}

export default connect(mapStateToProps, actions)(AppInstallationScrAppBookPage);
